/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 23/11/21
 */

import React from 'react';
import NewWorkplan from '@/components/pages/workplan/New';

import NotFound from '@/components/pages/NotFound';
import LoginCallback from '@/components/auth/LoginCallback';
import Admin from '@/components/pages/Admin/Admin';
import Dashboard from '@/components/pages/dashboard/Dashboard';
import Privacy from '@/components/pages/privacy/Privacy';
import ActivityWorkplanItem from '@/components/pages/workplan/workplanItemAction/Actions/ActivityAction';
import GoalWorkplanItem from '@/components/pages/workplan/workplanItemAction/Actions/GoalAction';
import LeaveWorkplanItem from '@/components/pages/workplan/workplanItemAction/Actions/PlannedLeaveAction';
import TrainingWorkplanItem from '@/components/pages/workplan/workplanItemAction/Actions/TrainingAction';
import UserSupervisionDetails from '@/components/pages/Admin/UserSupervision/UserSupervisionDetails';
import UserSupervisionWorkplan from '@/components/pages/Admin/UserSupervision/Workplan/UserSupervisionWorkplan';
import ConsiderationWorkplanItem from '@/components/pages/workplan/workplanItemAction/Actions/ConsiderationAction';
import NavigateProvider from '@/components/contexts/NaviateContext/NavigateProvider';
import { RouteWorkplanItem } from '@/components/pages/workplanItemHandler/WorkplanItemHandler';
import Supervisor from '@/components/pages/supervisor/Supervisor';

export interface CustomRoute {
    path: string;
    element: React.ReactNode;
    key: string;
    authRequired: boolean;
}

const routes: CustomRoute[] = [
    {
        path: '/',
        element: (
            <NavigateProvider prefix="/workplan" workplanActions>
                <Dashboard />
            </NavigateProvider>
        ),
        key: 'dashboard',
        authRequired: true,
    },
    {
        path: '/workplan/:workplanId/:version',
        element: (
            <NavigateProvider prefix="/workplan" workplanActions>
                <Dashboard />
            </NavigateProvider>
        ),
        key: 'new-work-plan-version',
        authRequired: true,
    },

    //Workplan create workplan item
    {
        path: '/workplan/:workplanId/:version/:type',
        element: (
            <NavigateProvider prefix="/workplan" mode="create">
                <RouteWorkplanItem readonly={false} />
            </NavigateProvider>
        ),
        key: 'workplan-new-item-version',
        authRequired: true,
    },

    //Workplan edit workplan item
    {
        path: '/workplan/:workplanId/:version/:type/:formId/edit',
        element: (
            <NavigateProvider prefix="/workplan" mode="edit">
                <RouteWorkplanItem readonly={false} />
            </NavigateProvider>
        ),
        key: 'workplan-edit-item',
        authRequired: true,
    },

    //Workplan view workplan item
    {
        path: '/workplan/:workplanId/:version/goal/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/workplan">
                <GoalWorkplanItem />
            </NavigateProvider>
        ),
        key: 'workplan-view-goal-version',
        authRequired: true,
    },
    {
        path: '/workplan/:workplanId/:version/activity/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/workplan">
                <ActivityWorkplanItem />
            </NavigateProvider>
        ),
        key: 'workplan-view-activity-version',
        authRequired: true,
    },
    {
        path: '/workplan/:workplanId/:version/planned-leave/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/workplan">
                <LeaveWorkplanItem />
            </NavigateProvider>
        ),
        key: 'workplan-view-planned-leave-version',
        authRequired: true,
    },
    {
        path: '/workplan/:workplanId/:version/training/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/workplan">
                <TrainingWorkplanItem />
            </NavigateProvider>
        ),
        key: 'workplan-view-training',
        authRequired: true,
    },
    {
        path: '/workplan/:workplanId/:version/consideration/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/workplan">
                <ConsiderationWorkplanItem />
            </NavigateProvider>
        ),
        key: 'workplan-view-consideration',
        authRequired: true,
    },

    /***** Supervisor *****/
    {
        path: '/supervisor',
        element: (
            <NavigateProvider prefix="/supervisor" supervisor>
                <Supervisor />
            </NavigateProvider>
        ),
        key: 'supervisor',
        authRequired: true,
    },
    {
        path: '/supervisor/workplan/:id/:version',
        element: (
            <NavigateProvider prefix="/supervisor/workplan" supervisor workplanActions>
                <NewWorkplan />
            </NavigateProvider>
        ),
        key: 'view-submitted-workplan-supervisor',
        authRequired: true,
    },
    {
        path: '/supervisor/workplan/:workplanId/:version/goal/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/supervisor/workplan" supervisor>
                <GoalWorkplanItem />
            </NavigateProvider>
        ),
        key: 'supervisor-view-goal-version',
        authRequired: true,
    },
    {
        path: '/supervisor/workplan/:workplanId/:version/activity/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/supervisor/workplan" supervisor>
                <ActivityWorkplanItem />
            </NavigateProvider>
        ),
        key: 'supervisor-view-activity-version',
        authRequired: true,
    },
    {
        path: '/supervisor/workplan/:workplanId/:version/planned-leave/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/supervisor/workplan" supervisor>
                <LeaveWorkplanItem />
            </NavigateProvider>
        ),
        key: 'supervisor-view-planned-leave-version',
        authRequired: true,
    },
    {
        path: '/supervisor/workplan/:workplanId/:version/training/:formId/:workplanItemId/',
        element: (
            <NavigateProvider prefix="/supervisor/workplan" supervisor>
                <TrainingWorkplanItem />
            </NavigateProvider>
        ),
        key: 'supervisor-view-training-version',
        authRequired: true,
    },
    {
        path: '/supervisor/workplan/:workplanId/:version/consideration/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/supervisor/workplan" supervisor>
                <ConsiderationWorkplanItem />
            </NavigateProvider>
        ),
        key: 'supervisor-view-consideration',
        authRequired: true,
    },
    {
        path: '/supervisor/workplans/:users/:assignmentId',
        element: (
            <NavigateProvider prefix="/supervisor/workplans/:users/:assignmentId" supervisor>
                <UserSupervisionDetails />
            </NavigateProvider>
        ),
        key: 'supervisor-user-supervision-details',
        authRequired: true,
    },
    {
        path: '/supervisor/workplans/:users/:assignmentId/workplan/:workplanId/:version',
        element: (
            <NavigateProvider prefix="/supervisor/workplans/:users/:assignmentId/workplan" supervisor workplanActions>
                <UserSupervisionWorkplan />
            </NavigateProvider>
        ),
        key: 'supervisor-user-supervision-details',
        authRequired: true,
    },
    {
        path: '/supervisor/workplans/:users/:assignmentId/workplan/:workplanId/:version/goal/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/supervisor/workplans/:users/:assignmentId/workplan" supervisor>
                <GoalWorkplanItem />
            </NavigateProvider>
        ),
        key: 'supervisor-view-goal',
        authRequired: true,
    },
    {
        path: '/supervisor/workplans/:users/:assignmentId/workplan/:workplanId/:version/activity/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/supervisor/workplans/:users/:assignmentId/workplan" supervisor>
                <ActivityWorkplanItem />
            </NavigateProvider>
        ),
        key: 'supervisor-view-activity',
        authRequired: true,
    },

    {
        path: '/supervisor/workplans/:users/:assignmentId/workplan/:workplanId/:version/planned-leave/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/supervisor/workplans/:users/:assignmentId/workplan" supervisor>
                <LeaveWorkplanItem />
            </NavigateProvider>
        ),
        key: 'supervisor-view-planned-leave',
        authRequired: true,
    },
    {
        path: '/supervisor/workplans/:users/:assignmentId/workplan/:workplanId/:version/training/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/supervisor/workplans/:users/:assignmentId/workplan" supervisor>
                <TrainingWorkplanItem />
            </NavigateProvider>
        ),
        key: 'supervisor-view-training',
        authRequired: true,
    },
    {
        path: '/supervisor/workplans/:users/:assignmentId/workplan/:workplanId/:version/consideration/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/supervisor/workplans/:users/:assignmentId/workplan" supervisor>
                <ConsiderationWorkplanItem />
            </NavigateProvider>
        ),
        key: 'supervisor-view-training',
        authRequired: true,
    },

    {
        path: '/login/callback',
        element: <LoginCallback />,
        key: 'login-callback',
        authRequired: false,
    },
    // route for privacy policy
    {
        path: '/privacy',
        element: <Privacy />,
        key: 'privacy',
        authRequired: false,
    },
    {
        path: '*',
        element: <NotFound />,
        key: 'not-found',
        authRequired: true,
    },

    /***** Admin *****/
    {
        path: '/admin',
        element: (
            <NavigateProvider>
                <Admin />
            </NavigateProvider>
        ),
        key: 'admin',
        authRequired: true,
    },
    {
        path: '/admin/supervision/:users/:assignmentId',
        element: (
            <NavigateProvider prefix="/admin/supervision/:users/:assignmentId" admin>
                <UserSupervisionDetails />
            </NavigateProvider>
        ),
        key: 'admin-user-supervision-details',
        authRequired: true,
    },
    {
        path: '/admin/supervision/:users/:assignmentId/workplan/:workplanId/:version',
        element: (
            <NavigateProvider prefix="/admin/supervision/:users/:assignmentId/workplan" admin workplanActions>
                <UserSupervisionWorkplan />
            </NavigateProvider>
        ),
        key: 'admin-user-supervision-details',
        authRequired: true,
    },
    {
        path: '/admin/supervision/:users/:assignmentId/workplan/:workplanId/:version/goal/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/admin/supervision/:users/:assignmentId/workplan" admin>
                <GoalWorkplanItem />
            </NavigateProvider>
        ),
        key: 'admin-view-goal',
        authRequired: true,
    },
    {
        path: '/admin/supervision/:users/:assignmentId/workplan/:workplanId/:version/activity/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/admin/supervision/:users/:assignmentId/workplan" admin>
                <ActivityWorkplanItem />
            </NavigateProvider>
        ),
        key: 'admin-view-activity',
        authRequired: true,
    },

    {
        path: '/admin/supervision/:users/:assignmentId/workplan/:workplanId/:version/planned-leave/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/admin/supervision/:users/:assignmentId/workplan" admin>
                <LeaveWorkplanItem />
            </NavigateProvider>
        ),
        key: 'admin-view-planned-leave',
        authRequired: true,
    },
    {
        path: '/admin/supervision/:users/:assignmentId/workplan/:workplanId/:version/training/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/admin/supervision/:users/:assignmentId/workplan" admin>
                <TrainingWorkplanItem />
            </NavigateProvider>
        ),
        key: 'admin-view-training',
        authRequired: true,
    },
    {
        path: '/admin/supervision/:users/:assignmentId/workplan/:workplanId/:version/consideration/:formId/:workplanItemId',
        element: (
            <NavigateProvider prefix="/admin/supervision/:users/:assignmentId/workplan" admin>
                <ConsiderationWorkplanItem />
            </NavigateProvider>
        ),
        key: 'admin-view-training',
        authRequired: true,
    },
];

export default routes;
